<template>
  <div class="steps pb-24" style="height: calc(100dvh - 9rem); height: calc(100vh - 9rem); overflow-y: auto;">
    <div v-if="storeData.type === 'company'">
      {{$tm('onboarding.kyc.borrowerTitle')}}
    </div>
    <div class="flex-grow">
      <div class="mt-16 md:mt-4">
        <template v-if="storeKycType">
          <div class="steps__title text-left">
            {{ $tm('onboarding.kyc.identity_proof.title') }}
          </div>
          <div class="steps__subtitle text-left" v-html="$t('onboarding.kyc.identity_proof.subtitle')"/>
        </template>
      </div>

      <!-- Document slot 1 -->
      <div class="mb-6">
        <p class="text-sm font-semibold text-grey-2 mb-1 text-left">
          {{ $t('onboarding.kyc.upload.document_n', { n: 1 }) }}
        </p>
        <p v-if="showFrontBack" class="text-xs text-grey-4 mb-2 text-left">
          {{ $t('onboarding.kyc.upload.front_back_instruction') }}
        </p>
        <div class="md:bg-white md:p-4 rounded-3xl">
          <file-upload
            ref="upload0"
            v-model="slot0Files"
            accept="image/png,image/jpeg,image/jpg,application/pdf"
            :extensions="['jpg', 'jpeg', 'png', 'pdf']"
            :size="1024 * 1024 * 7"
            @dragenter="detectDrag(true)"
            @dragleave="detectDrag(false)"
            input-id="kycfile1"
            @mouseleave="detectDrag(false)"
            @dragover="moveCursor"
            post-action="/upload/post"
            :multiple="true"
            :maximum="2"
            :thread="5"
            :drop="'.drop-area-0'"
            class="steps__upload relative w-full cursor-pointer"
            :class="{'opacity-50 cursor-not-allowed pointer-events-none': slot0Files.length >= 2}"
          >
            <div class="drop-area drop-area-0 w-full h-full z-50 absolute" @click.prevent="addFile('kycfile1')"></div>
            <div class="absolute top-2/4 md:left-2/4 left-4 transform md:w-full -translate-y-1/2 md:-translate-x-1/2">
              <div class="flex justify-center items-center">
                <div class="w-5 mr-2"><img src="@/assets/ico/onboarding/upload.svg" alt="" /></div>
                <div class="text-sm md:uppercase">{{ $tm('onboarding.kyc.upload.upload_files') }}</div>
              </div>
            </div>
            <div :style="cursorCircle" class="g-cursor__circle" :class="isDragging ? '' : 'opacity-0'"></div>
          </file-upload>
          <div v-for="(file, i) in slot0Files" :key="file">
            <div class="py-2 px-4 flex justify-between font-medium mt-4 text-grey-3 text-sm items-center"
              :class="validImg(file) ? 'steps__upload--success' : 'steps__upload--error'">
              <div>{{ file.name }} - {{ formatSize(file.size) }}</div>
              <div class="w-4 mt-1 cursor-pointer" @click="slot0Files.splice(i, 1)">
                <img src="@/assets/svg/garbage.svg" alt="" />
              </div>
            </div>
            <div class="card__card-input__error" style="margin-bottom: 0px">{{ errorMessage(file) }}</div>
          </div>
        </div>
      </div>

      <!-- Document slot 2 -->
      <div class="mb-6">
        <p class="text-sm font-semibold text-grey-2 mb-1 text-left">
          {{ $t('onboarding.kyc.upload.document_n', { n: 2 }) }}
        </p>
        <p v-if="showFrontBack" class="text-xs text-grey-4 mb-2 text-left">
          {{ $t('onboarding.kyc.upload.front_back_instruction') }}
        </p>
        <div class="md:bg-white md:p-4 rounded-3xl">
          <file-upload
            ref="upload1"
            v-model="slot1Files"
            accept="image/png,image/jpeg,image/jpg,application/pdf"
            :extensions="['jpg', 'jpeg', 'png', 'pdf']"
            :size="1024 * 1024 * 7"
            @dragenter="detectDrag(true)"
            @dragleave="detectDrag(false)"
            input-id="kycfile2"
            @mouseleave="detectDrag(false)"
            @dragover="moveCursor"
            post-action="/upload/post"
            :multiple="true"
            :maximum="2"
            :thread="5"
            :drop="'.drop-area-1'"
            class="steps__upload relative w-full cursor-pointer"
            :class="{'opacity-50 cursor-not-allowed pointer-events-none': slot1Files.length >= 2}"
          >
            <div class="drop-area drop-area-1 w-full h-full z-50 absolute" @click.prevent="addFile('kycfile2')"></div>
            <div class="absolute top-2/4 md:left-2/4 left-4 transform md:w-full -translate-y-1/2 md:-translate-x-1/2">
              <div class="flex justify-center items-center">
                <div class="w-5 mr-2"><img src="@/assets/ico/onboarding/upload.svg" alt="" /></div>
                <div class="text-sm md:uppercase">{{ $tm('onboarding.kyc.upload.upload_files') }}</div>
              </div>
            </div>
            <div :style="cursorCircle" class="g-cursor__circle" :class="isDragging ? '' : 'opacity-0'"></div>
          </file-upload>
          <div v-for="(file, i) in slot1Files" :key="file">
            <div class="py-2 px-4 flex justify-between font-medium mt-4 text-grey-3 text-sm items-center"
              :class="validImg(file) ? 'steps__upload--success' : 'steps__upload--error'">
              <div>{{ file.name }} - {{ formatSize(file.size) }}</div>
              <div class="w-4 mt-1 cursor-pointer" @click="slot1Files.splice(i, 1)">
                <img src="@/assets/svg/garbage.svg" alt="" />
              </div>
            </div>
            <div class="card__card-input__error" style="margin-bottom: 0px">{{ errorMessage(file) }}</div>
          </div>
        </div>
      </div>

      <!-- Document slots 3 & 4 — companies only -->
      <template v-if="storeData.type === 'company'">
        <div class="mb-6">
          <p class="text-sm font-semibold text-grey-2 mb-1 text-left">
            {{ $t('onboarding.kyc.upload.document_n', { n: 3 }) }}
          </p>
          <p v-if="showFrontBack" class="text-xs text-grey-4 mb-2 text-left">
            {{ $t('onboarding.kyc.upload.front_back_instruction') }}
          </p>
          <div class="md:bg-white md:p-4 rounded-3xl">
            <file-upload
              ref="upload2"
              v-model="slot2Files"
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              :extensions="['jpg', 'jpeg', 'png', 'pdf']"
              :size="1024 * 1024 * 7"
              @dragenter="detectDrag(true)"
              @dragleave="detectDrag(false)"
              input-id="kycfile3"
              @mouseleave="detectDrag(false)"
              @dragover="moveCursor"
              post-action="/upload/post"
              :multiple="true"
              :maximum="2"
              :thread="5"
              :drop="'.drop-area-2'"
              class="steps__upload relative w-full cursor-pointer"
              :class="{'opacity-50 cursor-not-allowed pointer-events-none': slot2Files.length >= 2}"
            >
              <div class="drop-area drop-area-2 w-full h-full z-50 absolute" @click.prevent="addFile('kycfile3')"></div>
              <div class="absolute top-2/4 md:left-2/4 left-4 transform md:w-full -translate-y-1/2 md:-translate-x-1/2">
                <div class="flex justify-center items-center">
                  <div class="w-5 mr-2"><img src="@/assets/ico/onboarding/upload.svg" alt="" /></div>
                  <div class="text-sm md:uppercase">{{ $tm('onboarding.kyc.upload.upload_files') }}</div>
                </div>
              </div>
              <div :style="cursorCircle" class="g-cursor__circle" :class="isDragging ? '' : 'opacity-0'"></div>
            </file-upload>
            <div v-for="(file, i) in slot2Files" :key="file">
              <div class="py-2 px-4 flex justify-between font-medium mt-4 text-grey-3 text-sm items-center"
                :class="validImg(file) ? 'steps__upload--success' : 'steps__upload--error'">
                <div>{{ file.name }} - {{ formatSize(file.size) }}</div>
                <div class="w-4 mt-1 cursor-pointer" @click="slot2Files.splice(i, 1)">
                  <img src="@/assets/svg/garbage.svg" alt="" />
                </div>
              </div>
              <div class="card__card-input__error" style="margin-bottom: 0px">{{ errorMessage(file) }}</div>
            </div>
          </div>
        </div>

        <div class="mb-6">
          <p class="text-sm font-semibold text-grey-2 mb-1 text-left">
            {{ $t('onboarding.kyc.upload.document_n', { n: 4 }) }}
          </p>
          <p v-if="showFrontBack" class="text-xs text-grey-4 mb-2 text-left">
            {{ $t('onboarding.kyc.upload.front_back_instruction') }}
          </p>
          <div class="md:bg-white md:p-4 rounded-3xl">
            <file-upload
              ref="upload3"
              v-model="slot3Files"
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              :extensions="['jpg', 'jpeg', 'png', 'pdf']"
              :size="1024 * 1024 * 7"
              @dragenter="detectDrag(true)"
              @dragleave="detectDrag(false)"
              input-id="kycfile4"
              @mouseleave="detectDrag(false)"
              @dragover="moveCursor"
              post-action="/upload/post"
              :multiple="true"
              :maximum="2"
              :thread="5"
              :drop="'.drop-area-3'"
              class="steps__upload relative w-full cursor-pointer"
              :class="{'opacity-50 cursor-not-allowed pointer-events-none': slot3Files.length >= 2}"
            >
              <div class="drop-area drop-area-3 w-full h-full z-50 absolute" @click.prevent="addFile('kycfile4')"></div>
              <div class="absolute top-2/4 md:left-2/4 left-4 transform md:w-full -translate-y-1/2 md:-translate-x-1/2">
                <div class="flex justify-center items-center">
                  <div class="w-5 mr-2"><img src="@/assets/ico/onboarding/upload.svg" alt="" /></div>
                  <div class="text-sm md:uppercase">{{ $tm('onboarding.kyc.upload.upload_files') }}</div>
                </div>
              </div>
              <div :style="cursorCircle" class="g-cursor__circle" :class="isDragging ? '' : 'opacity-0'"></div>
            </file-upload>
            <div v-for="(file, i) in slot3Files" :key="file">
              <div class="py-2 px-4 flex justify-between font-medium mt-4 text-grey-3 text-sm items-center"
                :class="validImg(file) ? 'steps__upload--success' : 'steps__upload--error'">
                <div>{{ file.name }} - {{ formatSize(file.size) }}</div>
                <div class="w-4 mt-1 cursor-pointer" @click="slot3Files.splice(i, 1)">
                  <img src="@/assets/svg/garbage.svg" alt="" />
                </div>
              </div>
              <div class="card__card-input__error" style="margin-bottom: 0px">{{ errorMessage(file) }}</div>
            </div>
          </div>
        </div>
      </template>

      <div class="text-grey-700 opacity-75 mt-3 mb-5 text-13" v-if="storeKycType === 'IDENTITY_PROOF'">
        {{ $tm('onboarding.kyc.upload.confirmation_message') }}
      </div>
      <Form class="form" ref="form" v-if="storeKycType === 'IDENTITY_PROOF'" v-slot="{ errors }">
        <GeneralInput
          v-model:data="data.name"
          type="text"
          name="name"
          :label="$tm('onboarding.kyc.upload.fields.first_name')"
          rules="required|maxLength:20"
          :locked="true"
          :noNumbersOrSymbols="true"
          :error="errors.name"
        />
        <GeneralInput
          v-model:data="data.surname"
          type="text"
          name="surname"
          :label="$tm('onboarding.kyc.upload.fields.last_name')"
          rules="required|maxLength:30"
          :error="errors.surname"
          :locked="true"
          :noNumbersOrSymbols="true"
          no-focus
        />
      </Form>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue'
import useRules from '@/assets/js/rules'
import FileUpload from 'vue-upload-component'
import { useI18n } from 'vue-i18n'
import GeneralInput from '@/components/general/Input.vue'
import { Form } from 'vee-validate'
import { useStore } from 'vuex'

export default {
  props: {
    kycType: {
      type: String,
      default: ''
    },
    kycUploadType: {
      type: String,
      default: ''
    },
    isActive: {
      type: Boolean,
      default: false
    },
    name: String,
    surname: String
  },
  components: {
    FileUpload,
    GeneralInput,
    Form
  },
  emits: ['nextKyc', 'dataChanged', 'generatedId', 'canContinue'],
  setup(props, { emit }) {
    const store = useStore()
    const i18n = useI18n({ useScope: 'global' })

    const data = ref({ name: '', surname: '' })
    const slot0Files = ref([])
    const slot1Files = ref([])
    const slot2Files = ref([])
    const slot3Files = ref([])
    let xParent = ref(0)
    let yParent = ref(0)
    const validTypes = ref([
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/pdf'
    ])
    let isDragging = ref(false)
    const form = ref(null)

    const { isRequired } = useRules()

    const cursorCircle = computed(() => {
      return `left: ${xParent.value}px ; top: ${yParent.value}px;`
    })

    const step = computed(() => store.state.onboarding.step)
    const storeData = computed(() => store.state.onboarding.data)
    const storeKycType = computed(() => store.state.onboarding.kyc_type)

    const isCompany = computed(() => storeData.value.type === 'company')
    // Front/back copy only applies to ID documents; company docs (registration etc.) don't have sides
    const showFrontBack = computed(() => !isCompany.value || storeKycType.value === 'IDENTITY_PROOF')

    function activeSlots() {
      const slots = [slot0Files.value, slot1Files.value]
      if (isCompany.value) slots.push(slot2Files.value, slot3Files.value)
      return slots
    }

    function errorMessage(file) {
      if (!validTypes.value.includes(file.type)) return i18n.t('errors.invalid_type')
      else if (file.size > 1024 * 1024 * 7) return i18n.t('errors.big_file')
      else if (file.size < 1024 * 32) return i18n.t('errors.small_file')
    }

    function validImg(file) {
      if (file) {
        return (
          validTypes.value.includes(file.type) &&
          file.size < 1024 * 1024 * 7 &&
          file.size > 1024 * 32
        )
      }
      return false
    }

    function formatSize(size) {
      if (size > 1024 * 1024 * 1024 * 1024) return (size / 1024 / 1024 / 1024 / 1024).toFixed(2) + ' TB'
      else if (size > 1024 * 1024 * 1024) return (size / 1024 / 1024 / 1024).toFixed(2) + ' GB'
      else if (size > 1024 * 1024) return (size / 1024 / 1024).toFixed(2) + ' MB'
      else if (size > 1024) return (size / 1024).toFixed(2) + ' KB'
      return size.toString() + ' B'
    }

    function addFile(inputId) {
      document.getElementById(inputId).click()
    }

    function moveCursor(e) {
      var proR = e.target.getBoundingClientRect()
      setTimeout(() => {
        xParent.value = e.clientX - proR.left - 35
        yParent.value = e.clientY - proR.top - 35
      }, 0)
    }

    function detectDrag(value) {
      isDragging.value = value
    }

    async function tryEmit() {
      const slots = activeSlots()
      // Individuals must fill every slot; companies only need slot 1, the rest are optional
      const allSlotsValid = slots.every((s, i) => (s.length > 0 || (isCompany.value && i > 0)) && s.every(f => validImg(f)))

      if (!allSlotsValid) {
        emit('canContinue', false)
        return
      }

      const allFiles = slots.flat()
      const payload = {
        kycFirst:   allFiles[0]?.file ?? null,
        kycSecond:  allFiles[1]?.file ?? null,
        kycThird:   allFiles[2]?.file ?? null,
        kycFourth:  allFiles[3]?.file ?? null,
        kycFifth:   allFiles[4]?.file ?? null,
        kycSixth:   allFiles[5]?.file ?? null,
        kycSeventh: allFiles[6]?.file ?? null,
        kycEighth:  allFiles[7]?.file ?? null,
      }

      if (storeKycType.value === 'IDENTITY_PROOF') {
        const validation = await form.value.validate()
        if (validation.valid) emit('canContinue', payload)
      } else {
        emit('canContinue', payload)
      }
    }

    watch(() => step.value, () => {
      if (step.value === 3) {
        data.value.name = storeData.value.name
        data.value.surname = storeData.value.surname
      }
    })

    watch(() => props.isActive, async () => {
      data.value.name = props.name ?? storeData.value.name
      data.value.surname = props.surname ?? storeData.value.surname

      if (storeData.value.type !== 'particular') {
        if (props.isActive) {
          store.dispatch('setKycType', storeData.value.kyc_type)
        } else {
          store.dispatch('setKycType', null)
        }
      }
    })

    watch(
      [slot0Files, slot1Files, slot2Files, slot3Files],
      async () => {
        if (props.isActive) {
          detectDrag(false)
          tryEmit()
        }
      },
      { deep: true }
    )

    watch(data.value, async () => {
      if (props.isActive) {
        storeData.value.name = data.value.name
        storeData.value.surname = data.value.surname
        tryEmit()
      }
    })

    return {
      data,
      cursorCircle,
      slot0Files,
      slot1Files,
      slot2Files,
      slot3Files,
      storeKycType,
      storeData,
      isCompany,
      showFrontBack,
      addFile,
      detectDrag,
      formatSize,
      validImg,
      isRequired,
      errorMessage,
      moveCursor,
      isDragging,
      form
    }
  }
}
</script>

<style scoped>
.steps::-webkit-scrollbar {
  display: none;
}
.steps {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
